import React, { useEffect, useState } from 'react';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';

const EMPTY = {
  subjectId:    '',
  type:         'individual',
  teacherId:    '',
  priceStudent: '',
  priceTeacher: '',
  archive:      false,
};

export default function ExtraLessonEditModal({ mode, lesson, subjects, teachers, onClose, onSaved }) {
  const [data, setData]     = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  useEffect(() => {
    if (mode === 'edit' && lesson) {
      setData({
        subjectId:    lesson.subject?.id ? String(lesson.subject.id) : '',
        type:         lesson.type || 'individual',
        teacherId:    lesson.teacher?.id ? String(lesson.teacher.id) : '',
        priceStudent: lesson.priceStudent != null ? String(lesson.priceStudent) : '',
        priceTeacher: lesson.priceTeacher != null ? String(lesson.priceTeacher) : '',
        archive:      !!lesson.archive,
      });
    } else {
      setData(EMPTY);
    }
  }, [mode, lesson?.id]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, saving]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  // Контекстный текст «Цена учителю» в зависимости от типа: и индивидуал,
  // и группа — это цена за ОДНОГО ученика. В групповом она умножается на
  // количество участников при проведении занятия.
  const priceTeacherHint = data.type === 'group'
    ? 'За одного ученика. Полная сумма учителю = эта цена × число участников группы.'
    : 'За одного ученика (он же единственный на занятии).';
  const priceStudentHint = data.type === 'group'
    ? 'С каждого ученика, отдельно. Группе — со всех одинаково.'
    : 'С ученика за это занятие.';

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError(null);
    try {
      const payload = {
        subjectId:    data.subjectId ? parseInt(data.subjectId, 10) : null,
        type:         data.type,
        teacherId:    data.teacherId ? parseInt(data.teacherId, 10) : null,
        priceStudent: data.priceStudent ? parseFloat(String(data.priceStudent).replace(',', '.')) : 0,
        priceTeacher: data.priceTeacher ? parseFloat(String(data.priceTeacher).replace(',', '.')) : 0,
        archive:      data.archive,
      };
      const res = mode === 'create'
        ? await api.post('/api/extra-lessons', payload)
        : await api.put(`/api/extra-lessons/${lesson.id}`, payload);
      onSaved?.(res.lesson);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  // Для предмета — показываем только активные. В edit-режиме допускаем
  // оставшийся выбор архивного предмета (если занятие старое).
  const visibleSubjects = (() => {
    const active = (subjects || []).filter((s) => !s.archive);
    if (mode === 'edit' && lesson?.subject?.archive
        && !active.find((s) => s.id === lesson.subject.id)) {
      return [...active, { ...lesson.subject, _legacy: true }];
    }
    return active;
  })();

  return (
    <div className="sd-overlay" onClick={(e) => { if (e.target.classList.contains('sd-overlay') && !saving) onClose(); }}>
      <div className="sd-modal sd-modal--narrow" role="dialog" aria-modal="true">
        <div className="sd-edit__head">
          <h2>{mode === 'create' ? 'Новое занятие' : 'Редактирование занятия'}</h2>
          <button className="sd-close" onClick={onClose} disabled={saving}><Icon name="x" size={14} /></button>
        </div>

        <form onSubmit={submit} className="sd-edit__form" noValidate>
          <div className="sd-edit__body">
            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Предмет *</label>
              <select
                className="sd-edit__select"
                value={data.subjectId}
                onChange={(e) => set('subjectId', e.target.value)}
                required
                autoFocus
              >
                <option value="">— выберите предмет —</option>
                {visibleSubjects.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}{s._legacy ? ' (в архиве)' : ''}
                  </option>
                ))}
              </select>
              {data.subjectId && (() => {
                const s = visibleSubjects.find((x) => String(x.id) === String(data.subjectId));
                if (!s) return null;
                if (!s.onec) {
                  return (
                    <div className="sd-edit__hint" style={{ color: 'var(--red-fg)' }}>
                      Предмет не привязан к номенклатуре 1С — платежи по этому занятию
                      не смогут уйти в 1С. Привяжите в разделе «Предметы».
                    </div>
                  );
                }
                return (
                  <div className="sd-edit__hint" style={{ color: 'var(--fg-muted)' }}>
                    1С: <span style={{ fontFamily: 'var(--font-mono)' }}>{s.onec.code}</span>
                    {s.onec.name ? ' · ' + s.onec.name : ''}
                  </div>
                );
              })()}
            </div>

            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Тип *</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <label className={'agr-type' + (data.type === 'individual' ? ' is-active' : '')} style={{ flex: 1 }}>
                  <input
                    type="radio"
                    name="type"
                    value="individual"
                    checked={data.type === 'individual'}
                    onChange={() => set('type', 'individual')}
                    style={{ display: 'none' }}
                  />
                  <div className="agr-type__main">
                    <div className="agr-type__label">Индивидуально</div>
                    <div className="agr-type__desc">Один ученик за раз</div>
                  </div>
                </label>
                <label className={'agr-type' + (data.type === 'group' ? ' is-active' : '')} style={{ flex: 1 }}>
                  <input
                    type="radio"
                    name="type"
                    value="group"
                    checked={data.type === 'group'}
                    onChange={() => set('type', 'group')}
                    style={{ display: 'none' }}
                  />
                  <div className="agr-type__main">
                    <div className="agr-type__label">Группа</div>
                    <div className="agr-type__desc">Несколько учеников</div>
                  </div>
                </label>
              </div>
            </div>

            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Учитель *</label>
              <select
                className="sd-edit__select"
                value={data.teacherId}
                onChange={(e) => set('teacherId', e.target.value)}
                required
              >
                <option value="">— выберите учителя —</option>
                {(teachers || []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                    {t.role === 'administrator' ? ' · администратор-совместитель' : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="sd-edit__field">
              <label className="sd-edit__label">Цена с ученика, ₽ *</label>
              <input
                className="sd-edit__input"
                type="number" step="0.01" min="0"
                value={data.priceStudent}
                onChange={(e) => set('priceStudent', e.target.value)}
                required
              />
              <div className="sd-edit__hint" style={{ color: 'var(--fg-muted)' }}>{priceStudentHint}</div>
            </div>
            <div className="sd-edit__field">
              <label className="sd-edit__label">Цена учителю, ₽ *</label>
              <input
                className="sd-edit__input"
                type="number" step="0.01" min="0"
                value={data.priceTeacher}
                onChange={(e) => set('priceTeacher', e.target.value)}
                required
              />
              <div className="sd-edit__hint" style={{ color: 'var(--fg-muted)' }}>{priceTeacherHint}</div>
            </div>

            <div className="sd-edit__field sd-edit__field--full">
              <label className="ref-checkbox">
                <input
                  type="checkbox"
                  checked={data.archive}
                  onChange={(e) => set('archive', e.target.checked)}
                />
                <span>В архиве (скрыто из выпадашек выбора)</span>
              </label>
            </div>
          </div>

          <div className="sd-edit__foot">
            {error && <div className="sd-edit__error">{error}</div>}
            <div className="sd-edit__foot-spacer" />
            <button type="button" className="btn btn--sm" onClick={onClose} disabled={saving}>Отмена</button>
            <button type="submit" className="btn btn--primary btn--sm" disabled={saving}>
              {saving ? 'Сохраняем…' : (mode === 'create' ? 'Создать' : 'Сохранить')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
